import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Family } from "@domain/product/monitoring/family/family";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { WizardComponent, WizardStepComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { buildUtilityGroupedSourceItems } from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getMonitoringOtherProductsLabel,
  getUtilityGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { filter, map, Observable, of, Subject, tap } from "rxjs";
import { switchMap } from "rxjs/operators";

export enum UtilitySelectorType {
  DEVICES = "DEVICES",
  OVERVIEW = "OVERVIEW",
}

export enum UtilityFilterLabel {
  LABEL = "LABEL",
  INPUT_PLACEHOLDER = "INPUT_PLACEHOLDER",
}

@Component({
  selector: "app-utility-configuration",
  imports: [
    AsyncPipe,
    ConfigurationWizardBackButtonComponent,
    OptionSelectorComponent,
    ProductConfigStepperBarComponent,
    WizardComponent,
    WizardStepComponent,
    MonitoringOverviewComponent,
  ],
  templateUrl: "./utility-configuration.component.html",
  styleUrl: "./utility-configuration.component.scss",
})
export class UtilityConfigurationComponent implements OnInit {
  protected UtilitySelectorTitleKeyMap: Record<UtilitySelectorType, string> = {
    [UtilitySelectorType.DEVICES]: $localize`:@@utilityConfiguration.step2.title:Select devices`,
    [UtilitySelectorType.OVERVIEW]: $localize`:@@utilityConfiguration.step3.title:Utility configuration summary`,
  };

  protected readonly overviewCategoryLabelMap: Partial<Record<UtilitySelectorType, string>> = {
    [UtilitySelectorType.DEVICES]: $localize`:@@utilityConfiguration.overview.table.column2.category.label:Devices`,
  };

  protected readonly filter1LabelMap: Record<UtilityFilterLabel, string> = {
    [UtilityFilterLabel.LABEL]: $localize`:@@utilityConfiguration.filter1.label:Categor 1y`,
    [UtilityFilterLabel.INPUT_PLACEHOLDER]: $localize`:@@utilityConfiguration.filter1.inputPlaceholder:Nach Kategorie 1 suchen...`,
  };

  protected readonly filter2LabelMap: Record<UtilityFilterLabel, string> = {
    [UtilityFilterLabel.LABEL]: $localize`:@@utilityConfiguration.filter2.label:Category 2`,
    [UtilityFilterLabel.INPUT_PLACEHOLDER]: $localize`:@@utilityConfiguration.filter2.inputPlaceholder:Nach Kategorie 2 suchen...`,
  };

  protected readonly overviewCategoryLabels = getUtilityGroupedPartLabels();

  protected project!: Project;
  protected originalConfiguration?: UtilityConfiguration;
  protected activeStep = 0;
  protected steps = 2;
  private hasAppliedInitialOriginalConfigSkip = false;

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly UtilitySelectorType = UtilitySelectorType;
  protected groupedSections: MonitoringGroupedPartSection[] = [];
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  private activeRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private monitoringProductService = inject(MonitoringProductService);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private productService = inject(ProductService);
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected selectedDevicesForm!: FormGroup;

  protected family?: Family;
  protected selectedFamily$?: Observable<Family>;
  protected selectedDevices?: OptionDisplayWithQuantity[];

  constructor() {
    this.overviewItems$
      .pipe(
        switchMap((items) =>
          items.length
            ? this.monitoringGroupedPartsService.buildSections(items, MonitoringCategory.UTILITY, this.otherProductsLabel)
            : of([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.utilities.find((utility) => utility.id === configId);
    }
    this.getAutoSelectedFamily();
  }

  private getAutoSelectedFamily() {
    this.selectedFamily$ = this.monitoringProductService
      .loadCategoryFamiliesFromDB(MonitoringCategory.UTILITY, this.productService.getFilterCountry())
      .pipe(
        map((families) => families[0]),
        filter((family): family is Family => !!family),
        tap((family) => (this.family = family)),
      );
  }

  protected setActiveStep(event: number) {
    if (event === 0 && this.originalConfiguration && !this.hasAppliedInitialOriginalConfigSkip) {
      this.hasAppliedInitialOriginalConfigSkip = true;
      this.configurationWizard.setActiveStepValid();
      this.configurationWizard.nextStep();
    } else {
      this.activeStep = event;
    }
  }

  protected setSelectedDevices($event: OptionDisplayWithQuantity[]) {
    this.selectedDevices = $event;
  }

  protected onSubmit() {
    if (this.activeStep === 0) {
      this.selectedDevicesForm.markAllAsTouched();
      if (!this.selectedDevicesForm.valid) {
        return;
      }
      this.collectOverviewData();
    }

    if (this.activeStep === 1) {
      if (this.originalConfiguration) {
        this.updateUtilityConfig(this.originalConfiguration);
      } else {
        this.createNewUtilityConfig();
      }
    }

    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  private updateUtilityConfig(config: UtilityConfiguration) {
    if (this.selectedDevices && this.selectedDevices.length >= 1 && this.family) {
      config.update(
        this.family.familyName,
        this.family.familyNumber,
        this.selectedDevices?.map((device) => MonitoringProductItem.create(device.optionDisplay.option, device.quantity)),
      );
    }
    this.backButton.navigateBack();
  }

  private createNewUtilityConfig() {
    if (this.selectedDevices && this.selectedDevices.length >= 1 && this.family) {
      const utilityConfig = MonitoringConfigurationFactory.createUtility(this.family.familyName, this.family, this.project, {
        devices: this.selectedDevices.map((device) => MonitoringProductItem.create(device.optionDisplay.option, device.quantity)),
      });

      this.project.addUtilities(utilityConfig);
    }
    this.backButton.navigateBack();
  }

  private collectOverviewData() {
    const items: MonitoringGroupedPartSourceItem[] = buildUtilityGroupedSourceItems(
      {
        devices: this.selectedDevices?.map((device) => ({ option: device.optionDisplay.option, quantity: device.quantity })),
      },
      this.overviewCategoryLabels,
    );

    if (!items.length) {
      this.overviewItems$.next([]);
      return;
    }

    this.overviewItems$.next(items);
  }

  protected readonly UtilityFilterLabel = UtilityFilterLabel;
}
